import type { KeptCover } from "daedalus-renderer-phaser";
import { BODY_HEIGHT, BODY_WIDTH, CONCEALMENT, EntityKind, concealedNext, concealingShare, worldUiHidden } from "odyssey-shared";
import type { DrawnCharacter } from "./remote-characters.ts";
import type { ConcealReading } from "./report.ts";

/**
 * The scene's judgement of which remote entities its viewer cannot see (ADR-061's amendment for P1.9-T73, R-0115):
 * each frame, after the world is posed, the fade has run and the roofs have answered for the local character, every
 * other player's character and creature drawn is judged concealed or not, behind a prop drawn over most of its body
 * or under a roof the viewer is not under, with the hysteresis of `CONCEALMENT`, and its world UI hidden unless it is
 * the viewer's target. The answers are written on the drawn characters, which `RemoteCharacters.forEach` hands to
 * every world UI surface.
 *
 * A player is measured by the one body box the occluder fade tests (`BODY_WIDTH` by `BODY_HEIGHT`, ADR-094 decision
 * 34), the same for every look; a creature by that width and the height of its own art.
 *
 * The cover measure, the costly part, is made in world space: at the entity's own position, its sixteenths times the
 * scale rounded to whole pixels, never where it is drawn, which moves by a pixel as the viewer walks. An entity is
 * measured again when that position moves, or when a new round begins: a round begins when the props' version moves
 * (`PropFade.version`: a prop view synced a change, or the fade changed an alpha) and at least every
 * `CONCEAL_REFRESH_MS`. A viewer walking past still props therefore measures nothing. At most
 * `CONCEAL_MEASURES_PER_FRAME` measurements are made a frame, starting each frame after the last entity measured, so a
 * crowd that all moves is measured over the next few frames, each keeping its last answer meanwhile. The roof test is a
 * few bitmap reads and runs for every entity every frame. Nothing is allocated.
 *
 * An entity whose feet, at its world position, stand in the local character's cone of sight (ADR-061's amendment for
 * P1.9-T75), while the cone acts (the amendment for P1.9-T82: in a dense place, from the first step of its strength's
 * ease in to the end of its ease out; in an open place nothing is in sight and a prop conceals whichever way the
 * viewer faces), is never concealed by a prop the cone can dim: its measured share is set aside for the judgement, the
 * frame the cone reaches it, while the roof test still applies. A prop the cone never dims still conceals it: a
 * building part, which never fades (ADR-040's amendment of 2026-10-03), and a thing, which the cone passes over (the
 * amendment for P1.9-T82). Each measurement also keeps the largest share one such prop hides (`keptCoverage`), and in
 * the cone that share is judged instead (`concealingShare`), so a player behind a house wall or a well ahead of the
 * viewer does not show through it. The point test is a few multiplications and runs for every entity
 * every frame.
 */

/**
 * The longest a cover measurement stands while nothing moves, in ms: a thing's frame changes without anything moving.
 */
export const CONCEAL_REFRESH_MS = 250;

/**
 * The most cover measurements a frame makes. Tunable.
 */
export const CONCEAL_MEASURES_PER_FRAME = 32;

/**
 * What the judgement reads: the cover measure and the props' version (`PropFade`), and the roof test
 * (`PresenceProps.roofedApart`).
 */
export interface ConcealSources {
  /**
   * The largest share of a body one prop drawn above it hides.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @param width The body's width in pixels.
   * @param height The body's height in pixels.
   * @param depth The depth the body draws at.
   * @param kept The share props the cone never dims give, raised in place.
   * @returns The share in `[0, 1]`.
   */
  mostCovered(x: number, y: number, width: number, height: number, depth: number, kept: KeptCover): number;
  /**
   * Whether a character stands under a roof the viewer is not under.
   *
   * @param x Feet x in whole sixteenths.
   * @param y Feet line in whole sixteenths.
   * @returns True when it does.
   */
  roofedApart(x: number, y: number): boolean;
  /**
   * A count that moves whenever the props could cover a character differently.
   *
   * @returns The count.
   */
  version(): number;
  /**
   * Whether a feet point stands in the local character's cone of sight while the cone acts.
   *
   * @param x Feet x in whole world pixels.
   * @param y Feet line in whole world pixels.
   * @returns True when the cone holds it.
   */
  inSight(x: number, y: number): boolean;
}

/**
 * Judges the remote entities drawn, frame by frame.
 */
export class Concealment {
  private round = 0;
  private roundMs = Number.NEGATIVE_INFINITY;
  private seenVersion = Number.NaN;
  private target = -1;
  private scale = 1;
  private measuredCount = 0;
  private budget = 0;
  private start = 0;
  private index = 0;
  private lastMeasured = -1;
  private wrapped = false;
  private readonly kept: KeptCover = { share: 0 };

  /**
   * Visits one drawn entity for the frame: measures it when it is stale, the frame's budget is not spent and its place
   * in the map falls in the pass under way (from the frame's start to the end, then from the beginning to the start),
   * and in the first pass asks the roof test; made once, so visiting the map allocates nothing.
   *
   * @param character The entity as drawn.
   */
  private readonly judgeOne = (character: DrawnCharacter): void => {
    const index = this.index;
    this.index += 1;
    const ours = this.wrapped ? index < this.start : index >= this.start;
    if (ours && this.budget > 0) {
      const x = Math.round(character.x * this.scale);
      const y = Math.round(character.y * this.scale);
      if (character.judgedRound !== this.round || character.judgedX !== x || character.judgedY !== y) {
        this.kept.share = 0;
        character.coverage = this.sources.mostCovered(x, y, BODY_WIDTH, character.kind === EntityKind.Player ? BODY_HEIGHT : Math.max(1, Math.round(character.head)), character.sprite.depth, this.kept);
        character.keptCoverage = this.kept.share;
        character.judgedRound = this.round;
        character.judgedX = x;
        character.judgedY = y;
        this.measuredCount += 1;
        this.budget -= 1;
        this.lastMeasured = index;
      }
    }
    if (this.wrapped) {
      return;
    }
    character.roofed = this.sources.roofedApart(Math.round(character.x), Math.round(character.y));
  };

  /**
   * Decides one entity's concealment from its last measurement, this frame's roof answer and whether its feet stand in
   * the cone of sight, which sets aside the measurement of every prop but one the cone never dims; made once.
   *
   * @param character The entity as drawn.
   * @param handle Its handle.
   */
  private readonly decideOne = (character: DrawnCharacter, handle: number): void => {
    character.sighted = this.sources.inSight(Math.round(character.x * this.scale), Math.round(character.y * this.scale));
    character.concealed = concealedNext(character.concealed, concealingShare(character.coverage, character.keptCoverage, character.sighted), character.roofed, CONCEALMENT);
    character.hidden = worldUiHidden(character.concealed, handle === this.target);
  };

  /**
   * @param sources The cover measure, the props' version and the roof test.
   */
  public constructor(private readonly sources: ConcealSources) {}

  /**
   * How many cover measurements have been made, for the report.
   *
   * @returns The count.
   */
  public get measured(): number {
    return this.measuredCount;
  }

  /**
   * Judges every remote entity drawn for this frame and writes the answers on them.
   *
   * @param characters The remote entities drawn, by handle.
   * @param target The viewer's target, or `NO_HANDLE` for none.
   * @param scale World pixels per sixteenth.
   * @param nowMs The client clock in ms.
   */
  public judge(characters: ReadonlyMap<number, DrawnCharacter>, target: number, scale: number, nowMs: number): void {
    const version = this.sources.version();
    if (version !== this.seenVersion || nowMs - this.roundMs >= CONCEAL_REFRESH_MS) {
      this.round += 1;
      this.roundMs = nowMs;
      this.seenVersion = version;
    }
    this.target = target;
    this.scale = scale;
    this.budget = CONCEAL_MEASURES_PER_FRAME;
    this.start = characters.size === 0 ? 0 : this.start % characters.size;
    this.lastMeasured = -1;
    this.index = 0;
    this.wrapped = false;
    characters.forEach(this.judgeOne);
    if (this.start > 0 && this.budget > 0) {
      this.index = 0;
      this.wrapped = true;
      characters.forEach(this.judgeOne);
      this.wrapped = false;
    }
    if (this.lastMeasured >= 0) {
      this.start = this.lastMeasured + 1;
    }
    characters.forEach(this.decideOne);
  }
}

/**
 * Every remote entity drawn with its judgement, for the report: made on request, never on a frame.
 *
 * @param characters The remote entities drawn, by handle.
 * @returns One reading an entity.
 */
export function concealReadings(characters: ReadonlyMap<number, DrawnCharacter>): ConcealReading[] {
  const readings: ConcealReading[] = [];
  for (const [handle, character] of characters) {
    readings.push({ handle, coverage: character.coverage, kept: character.keptCoverage, roofed: character.roofed, sighted: character.sighted, concealed: character.concealed, hidden: character.hidden });
  }
  return readings;
}
